import React from 'react'
import styled from 'styled-components'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import { line, radius, font, surface, cardStyle, transition, lowerLabel } from 'features/dashboard/tokens'
import { ENTITY_COLORS } from 'constants/entityColors'

const MANAGE_PATH = {
  portfolio: id => `/portfolios/${id}`,
  estate:    id => `/estates/${id}`,
  bp:        id => `/bp-list/${id}`,
}

const TYPE_META = {
  portfolio: { label: 'Portfolio',        plural: 'Portfolios',         Icon: AccountTreeRoundedIcon, tint: ENTITY_COLORS.portfolio },
  estate:    { label: 'Estate',           plural: 'Estates',            Icon: ApartmentRoundedIcon,   tint: ENTITY_COLORS.estate },
  bp:        { label: 'Building Passport', plural: 'Building Passports', Icon: HomeWorkRoundedIcon,    tint: ENTITY_COLORS.bp },
  unit:      { label: 'Unit Passport',    plural: 'Unit Passports',     Icon: MeetingRoomRoundedIcon, tint: ENTITY_COLORS.unit },
}

const ORDER = { portfolio: 0, estate: 1, bp: 2, unit: 3 }

const PortfolioNavigator = ({ label = 'Portfolio', entities = [], onOpen, onManage }) => {
  if (!entities.length) return null

  const sorted = [...entities].sort((a, b) =>
    (ORDER[a.type] ?? 9) - (ORDER[b.type] ?? 9) || (a.name || '').localeCompare(b.name || ''))

  const types = [...new Set(entities.map(e => e.type))]
  const heading = types.length === 1
    ? `Your ${lowerLabel(TYPE_META[types[0]]?.plural || `${label}s`)}`
    : types.includes('estate')
      ? 'Your portfolios, estates & buildings'
      : 'Your portfolios & buildings'

  return (
    <Wrap>
      <Head>
        <Eyebrow>{heading}</Eyebrow>
        <Hint>Click any card to view it · ↗ opens its full page</Hint>
      </Head>
      <List>
        {sorted.map(entity => {
          const meta = TYPE_META[entity.type] || TYPE_META.bp
          const Icon = meta.Icon
          const canManage = !!MANAGE_PATH[entity.type]
          return (
            <Row key={entity.id} $tint={meta.tint}>
              <CardBtn
                type='button'
                onClick={() => onOpen?.(entity)}
                title={`View ${entity.name}`}
                $hasCorner={canManage}
              >
                <RowIcon $tint={meta.tint} aria-hidden='true'>
                  <Icon style={{ fontSize: 18 }} />
                </RowIcon>
                <RowMain>
                  <RowName>{entity.name}</RowName>
                  <RowType $tint={meta.tint}>{meta.label}</RowType>
                </RowMain>
              </CardBtn>
              {canManage && (
                <OpenCorner
                  type='button'
                  $tint={meta.tint}
                  onClick={() => onManage?.(entity)}
                  title={`Open the full ${lowerLabel(meta.label)} page`}
                  aria-label={`Open the full ${lowerLabel(meta.label)} page`}
                >
                  <LaunchRoundedIcon style={{ fontSize: 15 }} />
                </OpenCorner>
              )}
            </Row>
          )
        })}
      </List>
    </Wrap>
  )
}

export default PortfolioNavigator

const Wrap = styled.section`
  ${cardStyle}
  padding: 18px 20px;
  margin-bottom: 16px;
`

const Head = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`

const Eyebrow = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Hint = styled.span`
  font-size: ${font.caption};
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
  border-radius: ${radius.pill};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
`

const Row = styled.div`
  position: relative;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-left: 3px solid ${({ $tint }) => $tint};
  border-radius: ${radius.md};
  overflow: hidden;
  transition: ${transition};

  &:hover {
    border-color: ${({ $tint }) => $tint};
    background: ${({ theme }) => theme.colors.white};
  }
`

const CardBtn = styled.button`
  font: inherit;
  display: flex; align-items: center; gap: 12px;
  width: 100%; min-width: 0;
  text-align: left;
  padding: 12px 14px;
  padding-right: ${({ $hasCorner }) => ($hasCorner ? '42px' : '14px')};
  background: transparent;
  border: none;
  cursor: pointer;
`

const OpenCorner = styled.button`
  position: absolute;
  top: 8px; right: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px;
  padding: 0;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  transition: ${transition};

  &:hover {
    color: ${({ $tint }) => $tint};
    border-color: ${({ $tint }) => $tint};
    background: ${({ $tint }) => `${$tint}12`};
  }
`

const RowIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px;
  border-radius: ${radius.sm};
  background: ${({ $tint }) => `${$tint}14`};
  color: ${({ $tint }) => $tint};
`

const RowMain = styled.span`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 2px;
`

const RowName = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`

const RowType = styled.span`
  font-size: ${font.caption};
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ $tint }) => $tint};
`
